import React, { useState, useEffect, useMemo } from 'react';
import { UserFormTypeRole } from '@/utils/user';
import { useSignupStore } from '../../stores/signupStore';
import SuccessHeader from '../SuccessHeader';
import { Modal } from '../ui/Modal';

import { UserVerificationContent } from '../userVerification/UserVerificationContent';
import BorrowerForm from './BorrowerForm';
import InvestorForm from './InvestorForm';
import { RoleSelectionStep } from './RoleSelectionStep';

import TermsStep from './TermsStep';
import { VerificationStep } from './VerificationStep';

type SignupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin?: () => void;
};

type SignupStep =
  | 'initial'
  | 'roleSelection'
  | 'borrowerSignUp'
  | 'investorSignUp'
  | 'terms'
  | 'emailVerification'
  | 'userVerification';

type ModalHeaderProps = {
  title: string;
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ title }) => {
  return (
    <div
      className="text-center text-black text-2xl font-medium break-words"
      style={{ fontFamily: 'var(--ep-font-avenir)' }}
    >
      {title}
    </div>
  );
};

export const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose, onSwitchToLogin }) => {
  const signupStore = useSignupStore();
  const selectedRole = signupStore.role;

  const [currentStep, setCurrentStep] = useState<SignupStep>('initial');

  // Compute the initial step when modal opens
  const initialStep = useMemo((): SignupStep => {
    if (selectedRole) {
      return selectedRole === UserFormTypeRole.BORROWER ? 'borrowerSignUp' : 'investorSignUp';
    }
    return 'roleSelection';
  }, [selectedRole]);

  // Reset step when modal opens or role changes
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentStep(initialStep);
    }
  }, [isOpen, initialStep]);

  const handleClose = () => {
    // Reset flow
    signupStore.reset();
    setCurrentStep('roleSelection');
    onClose();
  };

  const handleRoleSelect = (role: UserFormTypeRole) => {
    signupStore.setRole(role);
    setCurrentStep(role === UserFormTypeRole.BORROWER ? 'borrowerSignUp' : 'investorSignUp');
  };

  const handleSwitchToLogin = () => {
    handleClose();
    onSwitchToLogin?.();
  };

  const handleTermsNext = () => {
    setCurrentStep('terms');
  };

  const handleTermsBack = (): SignupStep => {
    return selectedRole === UserFormTypeRole.BORROWER ? 'borrowerSignUp' : 'investorSignUp';
  };
  // Handle form completion - move to verification step
  const handleFormNext = () => {
    setCurrentStep('emailVerification');
  };

  const getHeader = () => {
    switch (currentStep) {
      case 'initial':
      case 'roleSelection':
      case 'borrowerSignUp':
      case 'investorSignUp':
        return <ModalHeader title="Sign Up" />;
      case 'emailVerification':
        return <ModalHeader title="Verify Your Contact Information" />;
      case 'userVerification':
        return <SuccessHeader title="Account created successfully!" />;
      default:
        return null;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'initial':
        return null;
      case 'roleSelection':
        return <RoleSelectionStep onRoleSelect={handleRoleSelect} onSwitchToLogin={handleSwitchToLogin} />;

      case 'borrowerSignUp':
        return (
          <BorrowerForm
            onBack={() => setCurrentStep('roleSelection')}
            onNext={handleTermsNext}
            onSwitchToLogin={handleSwitchToLogin}
          />
        );

      case 'investorSignUp':
        return (
          <InvestorForm
            onBack={() => setCurrentStep('roleSelection')}
            onNext={handleTermsNext}
            onSwitchToLogin={handleSwitchToLogin}
          />
        );
      case 'terms':
        return <TermsStep onBack={() => setCurrentStep(handleTermsBack())} onNext={handleFormNext}></TermsStep>;

      case 'emailVerification':
        if (!selectedRole) return null;
        return (
          <VerificationStep
            onBack={() => setCurrentStep('terms')}
            onSuccess={() => {
              setCurrentStep('userVerification');
            }}
          />
        );

      case 'userVerification':
        return <UserVerificationContent onComplete={handleClose} onSkip={handleClose} />;

      default:
        return null;
    }
  };

  const getModalMinHeight = () => {
    if (currentStep === 'investorSignUp') return 640;
    if (currentStep === 'userVerification') return 500;
    if (currentStep === 'emailVerification') return 400;
    return 580;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} minHeight={getModalMinHeight()} header={getHeader()}>
      {renderStep()}
    </Modal>
  );
};
